import React from "react";
import Container from "react-bootstrap/Container";
import "./Privacy.css";

export default function Privacy() {
  return (
    <Container as="main" className="privacy-page">
      <h1 className="page-title d-flex justify-content-center flex-nowrap">
        Privacy Policy
      </h1>
      <div className="page-content">
        <p>
          <strong>Last Updated: August 12, 2026</strong>
        </p>
        <p>
          Lumiwealth, Inc. (&quot;Lumiwealth,&quot; &quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;) operates BotSpot. This Privacy
          Policy explains how we collect, use, disclose, retain, and protect
          information when you use the BotSpot mobile application,{" "}
          <a href="https://botspot.trade">botspot.trade</a>,{" "}
          <a href="https://lumiwealth.com">lumiwealth.com</a>, and related
          services (collectively, the &quot;Services&quot;).
        </p>

        <h2>1. Information We Collect</h2>
        <p>
          We collect information that you provide, information created when you
          use BotSpot, and limited technical information needed to operate and
          protect the Services.
        </p>
        <h3>Account, Profile, and Onboarding Information</h3>
        <p>
          This may include your name, email address, phone number, nickname,
          country or location, biography, trading experience, sign-in provider
          and account identifier, discovery source, product goals, workshop
          registration details, and other information you choose to add. Some
          fields are optional, but information required for authentication or a
          requested feature must be provided to use that feature.
        </p>
        <h3>Trading, Broker, and Connection Information</h3>
        <p>
          If you connect a broker, exchange, messaging service, or external
          agent, we process the provider name, account identifiers and labels,
          paper or live account selection, authorization tokens or API
          credentials, connection status, and information returned through that
          connection. Depending on the feature, this can include balances,
          positions, orders, trades, deployments, alerts, Discord or Telegram
          connection details, and external agent API keys.
        </p>
        <h3>Content You Create</h3>
        <p>
          We process prompts, messages, conversation history, generated
          responses, trading ideas, strategy rules, bot configurations, code,
          backtest inputs and results, saved deployment variables, feedback,
          survey responses, and support communications that you submit or create
          through the Services. Saved variables and credentials may contain
          information you consider confidential, so submit only what is needed
          for the feature.
        </p>
        <h3>Purchases and Payouts</h3>
        <p>
          For mobile subscriptions, Apple or Google processes your payment
          method. BotSpot and RevenueCat receive purchase and entitlement
          information, such as the product, transaction status, subscription
          status, and a BotSpot account identifier; we do not receive your full
          payment-card number from the app store. If you participate in an
          affiliate payout program, Stripe processes payout onboarding and
          payment information under its own privacy policy.
        </p>
        <h3>Technical and Usage Information</h3>
        <p>
          Our systems and service providers may process information such as IP
          address, device and operating-system information, app version, access
          times, requested screens or features, product interactions, error
          information, and security events. We use this information to deliver,
          secure, diagnose, and improve the Services.
        </p>
        <h3>Website Analytics and Advertising Technology</h3>
        <p>
          On our websites, configured analytics and advertising tools may use
          cookies, local storage, pixels, and similar identifiers to process
          page views, page transitions, product interactions, referring
          information, approximate location derived from IP address, browser and
          device information, and campaign or conversion events. Current website
          integrations can include Google Tag Manager and Google Analytics, Meta
          Pixel, and PostHog. PostHog can also record website sessions and page
          interactions; sensitive event properties are filtered before
          transmission. These tools support analytics, feature flags, service
          improvement, attribution, and advertising measurement. Availability
          and retention depend on provider configuration and applicable law.
          Browser controls, supported provider controls, and Do Not Track
          settings may limit some processing; PostHog is configured to respect
          Do Not Track.
        </p>

        <h2>2. How We Use Information</h2>
        <p>
          We use information for the purposes described below and for other
          compatible purposes disclosed when information is collected.
        </p>
        <ul>
          <li>Authenticate you and create, secure, and manage your account.</li>
          <li>
            Provide AI-assisted creation, backtesting, marketplace, deployment,
            monitoring, broker, messaging, support, and other requested
            features.
          </li>
          <li>
            Process subscriptions, confirm entitlements, administer affiliate
            payouts, and keep transaction records.
          </li>
          <li>Personalize your profile and product experience.</li>
          <li>
            Send account, security, service, support, and transaction
            communications.
          </li>
          <li>
            Measure product use, diagnose problems, develop and improve the
            Services, and prevent fraud or abuse.
          </li>
          <li>
            Comply with law, enforce our agreements, resolve disputes, and
            protect users, Lumiwealth, and others.
          </li>
        </ul>

        <h2>3. AI-Assisted Features</h2>
        <p>
          When you use Agent or another AI-assisted feature, BotSpot sends the
          prompt or content you submit, relevant conversation context, and
          service metadata needed to fulfill the request to our backend and to
          OpenAI or Google, depending on the model configured for the feature.
          They process the information to generate or evaluate a response for
          BotSpot. The mobile app asks for your permission before the first
          transfer and lets you withdraw that permission from Agent.
        </p>
        <p>
          Do not place broker credentials, account passwords, payment-card
          details, government identifiers, or unrelated personal information in
          an AI prompt. AI output can be inaccurate and should be reviewed
          before you use, backtest, or deploy it.
        </p>

        <h2>4. When We Disclose Information</h2>
        <p>
          We disclose information only as needed for the Services, at your
          direction, or for legal and safety purposes.
        </p>
        <h3>Service Providers</h3>
        <p>
          We use providers for authentication, hosting, infrastructure,
          security, email, support, AI processing, app-store purchase and
          entitlement processing, analytics, advertising measurement, and
          affiliate payouts. Current integrations can include Auth0 for
          authentication, OpenAI or Google for AI processing, Apple or Google
          for app-store transactions, RevenueCat for subscription entitlements,
          Stripe for affiliate payout onboarding, Google Tag Manager and Google
          Analytics, Meta Pixel, and PostHog. These providers process
          information for the service they supply and under their own terms and
          privacy notices.
        </p>
        <h3>Connections You Choose</h3>
        <p>
          When you connect a broker, exchange, Discord, Telegram, external
          agent, or another third-party service, we exchange the information
          necessary to create and operate that connection. The third
          party&apos;s own terms and privacy policy also apply.
        </p>
        <h3>Legal, Safety, and Business Events</h3>
        <p>
          We may disclose information when reasonably necessary to comply with
          law or legal process; investigate fraud, abuse, security incidents, or
          policy violations; protect rights, property, or safety; or complete a
          merger, financing, acquisition, reorganization, or sale of assets
          subject to appropriate confidentiality and notice requirements.
        </p>

        <h2>5. Broker Credentials and Trading Data</h2>
        <p>
          BotSpot uses broker authorization and trading data to show account
          information and perform the paper or live actions you direct. The
          mobile app masks secret fields and uses a system browser for supported
          broker OAuth flows. You can remove a saved broker connection in
          Settings. Removing a connection from BotSpot does not close your
          brokerage account or cancel instructions already submitted to the
          broker.
        </p>

        <h2>6. Retention</h2>
        <p>
          We retain account, profile, purchase, trading, and user-content
          information while your account is active and as needed to provide the
          Services. Connection credentials are retained until you remove the
          connection, replace the credential, or delete the account, subject to
          backup and legal-retention requirements.
        </p>
        <p>
          After information is no longer needed for the purposes described here,
          we delete or de-identify it unless a longer period is reasonably
          required for legal, tax, accounting, fraud-prevention, security,
          dispute-resolution, or enforcement purposes. Residual copies may
          remain in protected backups until overwritten under our backup cycle.
        </p>

        <h2>7. Security</h2>
        <p>
          We use administrative, technical, and physical safeguards designed to
          protect personal information. The mobile app stores its local
          authentication session in device-protected secure storage and clears
          local account caches when you sign out. No safeguard or transmission
          method is completely secure, so we cannot guarantee absolute security.
        </p>

        <h2>8. Your Choices and Account Deletion</h2>
        <p>
          You can update profile information and remove supported broker,
          messaging, saved-variable, and external-access connections from
          Settings. Where Settings offers an enabled Apple or Google disconnect
          action, BotSpot requires a recent sign-in using another verified
          method and reports success only after the provider operation, durable
          sign-in blocking, and token revocation are confirmed. If BotSpot
          cannot confirm all those steps, it keeps the connection and reports
          that nothing was disconnected. You can also revoke third-party OAuth
          access through that provider&apos;s account controls.
        </p>
        <ul>
          <li>
            Use Settings → Delete account to initiate permanent deletion in the
            app. BotSpot requires fresh sign-in and exact typed confirmation,
            then records an idempotent deletion request. A queued request is not
            completed deletion.
          </li>
          <li>
            Deleting your BotSpot account does not automatically cancel a
            subscription. Manage App Store or Google Play subscriptions in your
            app-store account and website subscriptions in your BotSpot web
            account before deleting the account.
          </li>
          <li>
            The deletion process removes or anonymizes associated data across
            BotSpot and its providers, subject to safe shutdown of active
            trading automation, provider processing, backups, and limited
            records retained where required by law or for the purposes described
            in the Retention section. Contact{" "}
            <a href="mailto:support@lumiwealth.com">support@lumiwealth.com</a>{" "}
            with questions about a queued request.
          </li>
        </ul>

        <h2>9. Children</h2>
        <p>
          The Services are intended for adults who are at least 18. We do not
          knowingly collect personal information from children. If you believe a
          child provided personal information to us, contact us so we can
          investigate and delete it as appropriate.
        </p>

        <h2>10. Changes and Contact</h2>
        <p>
          We may update this Privacy Policy as the Services or our practices
          change. We will update the effective date and provide any additional
          notice required by law.
        </p>
        <p>
          For privacy questions, access or correction requests, or
          account-deletion requests, contact Lumiwealth, Inc. at{" "}
          <a href="mailto:support@lumiwealth.com">support@lumiwealth.com</a>.
        </p>
      </div>
    </Container>
  );
}
